import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PlayCircle } from 'lucide-react';
import API from '../api';
import './Home.css';

/* Página principal, muestra un banner, episodios recientes y catálogo paginado */
const Home = () => {
  const [animes, setAnimes] = useState([]);
  const [loading, setLoading] = useState(true);

  // --- ESTADOS PARA LA PAGINACIÓN ---
  const [paginaActual, setPaginaActual] = useState(1);
  const animesPorPagina = 24;

  useEffect(() => {
    let isMounted = true;
    /* Cargar animes */
    const fetchAnimes = async () => {
      try {
        const res = await API.get('/animes');
        if (isMounted) setAnimes(res.data);
      } catch (err) {
        console.error("Error cargando animes", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchAnimes();
    return () => { isMounted = false; };
  }, []);

  if (loading) return <div className="home-loading">Cargando catálogo...</div>;

  // --- LÓGICA DE PAGINACIÓN (TENDENCIAS) ---
  const indiceUltimoAnime = paginaActual * animesPorPagina;
  const indicePrimerAnime = indiceUltimoAnime - animesPorPagina;
  const animesPaginados = animes.slice(indicePrimerAnime, indiceUltimoAnime);
  const totalPaginas = Math.ceil(animes.length / animesPorPagina);

  const cambiarPagina = (numeroPagina) => {
    setPaginaActual(numeroPagina);
    document.getElementById('tendencias-titulo')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="home-container">
      {/* HERO */}
      <div className="hero">
        <img src="https://i0.wp.com/elpalomitron.com/wp-content/uploads/2020/01/pel%C3%ADcula-Fatestay-night-Heavens-Feel-III.-spring-song-destacada-El-Palomitr%C3%B3n.jpg?resize=1200%2C600&ssl=1" alt="banner" className="hero-bg" />
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1>Bienvenidos a AnimeWeb</h1>
          <div className="hero-tags"><span>Disfruta tu visita</span></div>
        </div>
      </div>

      {/* EPISODIOS RECIENTES */}
      <h2 className="section-title">Episodios recientes</h2>
      <div className="scroll-container">
        {(() => {
          const todosLosEpisodios = animes.flatMap(anime => 
            (anime.episodios || []).map(ep => ({
              ...ep,
              animeId: anime._id,
              animeTitulo: anime.titulo,
              animeImagen: anime.imagen
            }))
          );

          // Límite de episodios recientes
          const episodiosRecientes = todosLosEpisodios
            .sort((a, b) => new Date(b.fechaSubida) - new Date(a.fechaSubida))
            .slice(0, 20); 

          if (episodiosRecientes.length === 0) {
            return <p className="no-data">No hay episodios recientes disponibles.</p>;
          }

          return episodiosRecientes.map(episodio => (
            <Link 
              key={`${episodio.animeId}-${episodio.numero}`} 
              to={`/anime/${episodio.animeId}?ep=${episodio.numero}`} 
              className="scroll-card"
            >
              <img src={episodio.animeImagen} alt={episodio.animeTitulo} />
              <p>{episodio.animeTitulo} - Ep {episodio.numero}</p>
            </Link>
          ));
        })()}
      </div>

      {/* ANIMES EN TENDENCIA */}
      <h2 id="tendencias-titulo" className="home-title">Catalogo De Animes</h2>
      <div className="home-grid">
        {animesPaginados.length > 0 ? (
          animesPaginados.map(anime => (
            <div key={anime._id} className="anime-card">
              <Link to={`/anime/${anime._id}`} className="anime-card-link">
                <div className="anime-img-wrapper">
                  <img src={anime.imagen} alt={anime.titulo} className="anime-img" loading="lazy" />
                </div>
                <div className="anime-card-body">
                  <h3 className="anime-card-title" title={anime.titulo}>{anime.titulo}</h3>
                  <p className="anime-status">
                    {anime.enEmision ? (
                      <><span className="status-dot emision">●</span> En Emisión</>
                    ) : (
                      <><span className="status-dot finalizado">●</span> Finalizado</>
                    )}
                  </p>
                  <button className="btn-home-view">
                    <PlayCircle size={14} />
                    VER AHORA
                  </button>
                </div>
              </Link>
            </div>
          ))
        ) : (
          <p className="no-data">No hay animes disponibles.</p>
        )}
      </div>

      {/* --- CONTROLES DE PAGINACIÓN --- */}
      {totalPaginas > 1 && (
        <div className="pagination-container">
          <button 
            onClick={() => cambiarPagina(paginaActual - 1)} 
            disabled={paginaActual === 1}
            className="pagination-btn"
          >
            &laquo;
          </button>

          {[...Array(totalPaginas)].map((_, index) => {
            const numero = index + 1;
            return (
              <button
                key={numero}
                onClick={() => cambiarPagina(numero)}
                className={`pagination-btn ${paginaActual === numero ? 'active' : ''}`}
              >
                {numero}
              </button>
            );
          })}

          <button 
            onClick={() => cambiarPagina(paginaActual + 1)} 
            disabled={paginaActual === totalPaginas}
            className="pagination-btn"
          >
            &raquo;
          </button>
        </div>
      )}
    </div>
  );
};

export default Home;